import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { filter, find, forEach, includes, map, pickBy, size, slice, take, uniqBy, values } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { ResponseError } from 'superagent';

import {
  browseExternalSlideStorage,
  createExternalSlideTransferJob,
  preflightExternalSlideTransferJob,
} from 'api/externalSlideTransfers';
import { DialogChromeFooter, DialogChromeHeader, dialogPrimaryActionSx } from 'components/atoms/Dialog/DialogChrome';
import FileTreeSelector, { FileTreeEntry } from 'components/atoms/FileTreeSelector';
import {
  ExternalSlideStorageBrowseEntry,
  ExternalSlideStorageBrowseResult,
  ExternalSlideTransferDestinationConflictPolicy,
  ExternalSlideTransferJobCreateRequest,
  ExternalSlideTransferPreflightResult,
  StudyExternalStorageBucketSetting,
} from 'interfaces/externalStorage';
import { formatBytes } from 'utils/helpers';
import useStudy from 'utils/useStudy';
import StudyCohortManifestStep from './StudyCohortManifestStep';
import UploadAsyncIngestHandoff from './UploadAsyncIngestHandoff';

const PAGE_SIZE = 100;
const RECURSIVE_PAGE_SIZE = 1000;
const WARNING_DISPLAY_LIMIT = 5;
const RENAME_CONFLICT_PAGE_SIZE = 10;
const CSV_FILE_NAME_PATTERN = /^[^/]+\.csv$/i;
const PATH_SEPARATOR_START_REGEX = /^\/+/;
const PATH_SEPARATOR_END_REGEX = /\/$/;
const INTERNAL_ERROR_MESSAGE = 'An error occurred, please contact Nucleai support';

const bucketSettingKey = (setting: StudyExternalStorageBucketSetting) => `${setting.providerName}:${setting.bucket}`;

const joinRemotePath = (prefix: string, path: string) => `${prefix}${path}`.replace(PATH_SEPARATOR_START_REGEX, '');

const getEntrySourcePath = (prefix: string, path: string) => {
  const normalizedPath = path.replace(PATH_SEPARATOR_START_REGEX, '');
  return prefix && normalizedPath.startsWith(prefix) ? normalizedPath : joinRemotePath(prefix, normalizedPath);
};

const getParentPrefix = (prefix: string) => {
  const pathSegments = filter(prefix.replace(PATH_SEPARATOR_END_REGEX, '').split('/'));
  return pathSegments.length > 1 ? `${pathSegments.slice(0, -1).join('/')}/` : '';
};

const isStructuredErrorMessage = (message?: unknown) => {
  if (message && typeof message === 'object') {
    return true;
  }

  if (typeof message !== 'string') {
    return false;
  }

  const trimmedMessage = message.trim();
  if (!trimmedMessage || !includes(['{', '['], trimmedMessage[0])) {
    return false;
  }

  try {
    JSON.parse(trimmedMessage);
    return true;
  } catch {
    return false;
  }
};

const getErrorMessage = (error?: ResponseError | null) => {
  const detail = error?.response?.body?.detail;
  const message = detail || error?.message;

  if (isStructuredErrorMessage(message)) {
    return INTERNAL_ERROR_MESSAGE;
  }

  return message || 'External storage could not be accessed. Please try again.';
};

interface LoadedFolderState {
  entries: ExternalSlideStorageBrowseEntry[];
  isLoading?: boolean;
  error?: ResponseError | null;
}

interface ScopedBrowseEntry extends ExternalSlideStorageBrowseEntry, FileTreeEntry {
  sourcePath: string;
}

interface Props {
  studyId: string;
  onClose: () => void;
}

const ExternalStorageTransferForm = ({ studyId, onClose }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { study, isLoading: isStudyLoading, isError: isStudyError } = useStudy(studyId);
  const loadMoreRecursiveEntriesMutateRef = React.useRef<(continuationToken: string) => void>();
  const [selectedBucketKey, setSelectedBucketKey] = useState('');
  const [browsePrefix, setBrowsePrefix] = useState('');
  const [additionalEntries, setAdditionalEntries] = useState<ExternalSlideStorageBrowseEntry[]>([]);
  const [nextContinuationToken, setNextContinuationToken] = useState<string>();
  const [recursiveEntries, setRecursiveEntries] = useState<ExternalSlideStorageBrowseEntry[]>([]);
  const [recursiveContinuationToken, setRecursiveContinuationToken] = useState<string>();
  const [loadedFoldersByPath, setLoadedFoldersByPath] = useState<Record<string, LoadedFolderState>>({});
  const [quickFilter, setQuickFilter] = useState('');
  const [selectedPaths, setSelectedPaths] = useState<string[]>([]);
  const [isSelectedFilesListOpen, setIsSelectedFilesListOpen] = useState(false);
  const [generateCsv, setGenerateCsv] = useState(false);
  const [csvFileName, setCsvFileName] = useState('');
  const [transferBatchSize, setTransferBatchSize] = useState('1');
  const [isCsvTouched, setIsCsvTouched] = useState(false);
  const [destinationConflictPolicy, setDestinationConflictPolicy] =
    useState<ExternalSlideTransferDestinationConflictPolicy>('fail');
  const [preflightResult, setPreflightResult] = useState<ExternalSlideTransferPreflightResult>();
  const [renameDestinationPaths, setRenameDestinationPaths] = useState<Record<string, string>>({});
  const [renameConflictPage, setRenameConflictPage] = useState(0);
  const [overwriteApproved, setOverwriteApproved] = useState(false);
  const [queuedTransferJobId, setQueuedTransferJobId] = useState<string>();

  const bucketSettings = filter(
    study?.settings?.externalStorage?.slideSourceBucketSettings || [],
    (setting) => setting.enabled
  );
  const selectedBucketSetting = find(bucketSettings, (setting) => bucketSettingKey(setting) === selectedBucketKey);

  const browseQuery = useQuery<ExternalSlideStorageBrowseResult, ResponseError>(
    [
      'external-slide-storage',
      studyId,
      selectedBucketSetting?.providerName,
      selectedBucketSetting?.bucket,
      browsePrefix,
    ],
    ({ signal }) =>
      browseExternalSlideStorage(
        studyId,
        selectedBucketSetting!.providerName,
        {
          bucket: selectedBucketSetting!.bucket,
          prefix: browsePrefix || undefined,
          pageSize: PAGE_SIZE,
          recursive: false,
        },
        signal
      ),
    {
      enabled: Boolean(selectedBucketSetting),
      retry: false,
      onSuccess: (result) => setNextContinuationToken(result.nextContinuationToken || undefined),
    }
  );

  const loadMoreMutation = useMutation(
    () =>
      browseExternalSlideStorage(studyId, selectedBucketSetting!.providerName, {
        bucket: selectedBucketSetting!.bucket,
        prefix: browsePrefix || undefined,
        continuationToken: nextContinuationToken,
        pageSize: PAGE_SIZE,
        recursive: false,
      }),
    {
      onSuccess: (result) => {
        setAdditionalEntries((currentEntries) => uniqBy([...currentEntries, ...result.entries], 'path'));
        setNextContinuationToken(result.nextContinuationToken || undefined);
      },
    }
  );

  const recursiveBrowseQuery = useQuery<ExternalSlideStorageBrowseResult, ResponseError>(
    ['external-slide-storage-recursive', studyId, selectedBucketSetting?.providerName, selectedBucketSetting?.bucket],
    ({ signal }) =>
      browseExternalSlideStorage(
        studyId,
        selectedBucketSetting!.providerName,
        {
          bucket: selectedBucketSetting!.bucket,
          pageSize: RECURSIVE_PAGE_SIZE,
          recursive: true,
        },
        signal
      ),
    {
      enabled: Boolean(selectedBucketSetting),
      retry: false,
      onSuccess: (result) => {
        setRecursiveEntries(result.entries);
        setRecursiveContinuationToken(result.nextContinuationToken || undefined);
      },
    }
  );

  const loadMoreRecursiveEntriesMutation = useMutation(
    (continuationToken: string) =>
      browseExternalSlideStorage(studyId, selectedBucketSetting!.providerName, {
        bucket: selectedBucketSetting!.bucket,
        continuationToken,
        pageSize: RECURSIVE_PAGE_SIZE,
        recursive: true,
      }),
    {
      onSuccess: (result) => {
        setRecursiveEntries((currentEntries) => uniqBy([...currentEntries, ...result.entries], 'path'));
        setRecursiveContinuationToken(result.nextContinuationToken || undefined);
      },
    }
  );

  const loadFolderMutation = useMutation(
    (folderPath: string) =>
      browseExternalSlideStorage(studyId, selectedBucketSetting!.providerName, {
        bucket: selectedBucketSetting!.bucket,
        prefix: folderPath,
        pageSize: PAGE_SIZE,
        recursive: false,
      }),
    {
      onMutate: (folderPath) => {
        setLoadedFoldersByPath((currentFolders) => ({
          ...currentFolders,
          [folderPath]: {
            entries: currentFolders[folderPath]?.entries || [],
            isLoading: true,
            error: null,
          },
        }));
      },
      onSuccess: (result, folderPath) => {
        setLoadedFoldersByPath((currentFolders) => ({
          ...currentFolders,
          [folderPath]: {
            entries: result.entries,
            isLoading: false,
            error: null,
          },
        }));
      },
      onError: (error: ResponseError, folderPath) => {
        setLoadedFoldersByPath((currentFolders) => ({
          ...currentFolders,
          [folderPath]: {
            entries: currentFolders[folderPath]?.entries || [],
            isLoading: false,
            error,
          },
        }));
      },
    }
  );

  const getTransferRequest = (): ExternalSlideTransferJobCreateRequest => ({
    bucket: selectedBucketSetting!.bucket,
    sourcePaths: selectedPaths,
    destinationConflictPolicy,
    ...(destinationConflictPolicy === 'rename'
      ? {
          renameDestinationPaths: pickBy(renameDestinationPaths, (destinationPath) => destinationPath.trim()),
        }
      : {}),
    transferredFilesCsv: generateCsv
      ? {
          enabled: true,
          fileName: csvFileName.trim(),
        }
      : undefined,
    transferBatchSize: Number(transferBatchSize),
  });

  const preflightMutation = useMutation(
    (request: ExternalSlideTransferJobCreateRequest) =>
      preflightExternalSlideTransferJob(studyId, selectedBucketSetting!.providerName, request),
    {
      onSuccess: (result) => {
        setPreflightResult(result);
        setOverwriteApproved(false);
        setRenameConflictPage(0);
      },
    }
  );

  const createTransferMutation = useMutation(
    (request: ExternalSlideTransferJobCreateRequest) =>
      createExternalSlideTransferJob(studyId, selectedBucketSetting!.providerName, request),
    {
      onSuccess: ({ jobId }) => {
        enqueueSnackbar(`External slide transfer ${jobId} was queued.`, { variant: 'success' });
        setQueuedTransferJobId(jobId);
      },
    }
  );

  useEffect(() => {
    loadMoreRecursiveEntriesMutateRef.current = loadMoreRecursiveEntriesMutation.mutate;
  }, [loadMoreRecursiveEntriesMutation.mutate]);

  useEffect(() => {
    if (!browseQuery.data) {
      return;
    }

    setNextContinuationToken(browseQuery.data.nextContinuationToken || undefined);
  }, [browseQuery.data]);

  useEffect(() => {
    if (!recursiveBrowseQuery.data) {
      return;
    }

    setRecursiveEntries(recursiveBrowseQuery.data.entries);
    setRecursiveContinuationToken(recursiveBrowseQuery.data.nextContinuationToken || undefined);
  }, [recursiveBrowseQuery.data]);

  useEffect(() => {
    if (!recursiveContinuationToken || recursiveBrowseQuery.isFetching || loadMoreRecursiveEntriesMutation.isLoading) {
      return;
    }

    loadMoreRecursiveEntriesMutateRef.current?.(recursiveContinuationToken);
  }, [loadMoreRecursiveEntriesMutation.isLoading, recursiveBrowseQuery.isFetching, recursiveContinuationToken]);

  const browseEntries = uniqBy([...(browseQuery.data?.entries || []), ...additionalEntries], 'path');
  const scopedEntries: ScopedBrowseEntry[] = map(browseEntries, (entry) => ({
    ...entry,
    id: getEntrySourcePath(browsePrefix, entry.path),
    sourcePath: getEntrySourcePath(browsePrefix, entry.path),
  }));
  const recursiveScopedEntries: ScopedBrowseEntry[] = map(recursiveEntries, (entry) => ({
    ...entry,
    id: getEntrySourcePath('', entry.path),
    sourcePath: getEntrySourcePath('', entry.path),
  }));
  const recursiveTree = useMemo(() => {
    const foldersByPath: Record<string, ScopedBrowseEntry[]> = {};
    const entriesByPath: Record<string, ScopedBrowseEntry> = {};

    forEach(recursiveScopedEntries, (fileEntry) => {
      const pathSegments = filter(fileEntry.sourcePath.split('/'));
      forEach(pathSegments.slice(0, -1), (pathSegment, index) => {
        const folderPath = `${pathSegments.slice(0, index + 1).join('/')}/`;
        entriesByPath[folderPath] = entriesByPath[folderPath] || {
          id: folderPath,
          path: folderPath,
          sourcePath: folderPath,
          name: pathSegment,
          entryType: 'folder',
        };
      });
      entriesByPath[fileEntry.sourcePath] = fileEntry;
    });

    forEach(values(entriesByPath), (entry) => {
      const parentPath = getParentPrefix(entry.sourcePath);
      foldersByPath[parentPath] = foldersByPath[parentPath] || [];
      foldersByPath[parentPath].push(entry);
    });

    return {
      foldersByPath,
      rootEntries: foldersByPath[browsePrefix] || foldersByPath[''] || [],
    };
  }, [browsePrefix, recursiveScopedEntries]);
  const treeRootEntries = uniqBy([...scopedEntries, ...recursiveTree.rootEntries], 'sourcePath');
  const isCsvFileNameValid = !generateCsv || CSV_FILE_NAME_PATTERN.test(csvFileName.trim());
  const parsedTransferBatchSize = Number(transferBatchSize);
  const isTransferBatchSizeValid = Number.isInteger(parsedTransferBatchSize) && parsedTransferBatchSize >= 1;
  const isTransferActionLoading = preflightMutation.isLoading || createTransferMutation.isLoading;
  const canReviewTransfer = Boolean(
    selectedBucketSetting && selectedPaths.length > 0 && isCsvFileNameValid && isTransferBatchSizeValid
  );
  const hasDestinationConflicts = Boolean(preflightResult?.destinationConflicts.length);
  const requiresOverwriteApproval = destinationConflictPolicy === 'overwrite' && hasDestinationConflicts;
  const renameConflicts = preflightResult?.destinationConflicts || [];
  const allRenamePathsProvided =
    renameConflicts.length > 0 &&
    filter(renameConflicts, (conflict) => renameDestinationPaths[conflict.sourcePath]?.trim()).length ===
      renameConflicts.length;
  const visibleRenameConflicts = slice(
    renameConflicts,
    renameConflictPage * RENAME_CONFLICT_PAGE_SIZE,
    (renameConflictPage + 1) * RENAME_CONFLICT_PAGE_SIZE
  );
  const renameConflictPageCount = Math.ceil(renameConflicts.length / RENAME_CONFLICT_PAGE_SIZE);
  const displayedPreflightWarnings = filter(
    preflightResult?.preflightWarnings || [],
    (warning) => warning.warningType !== 'identical_active_transfer'
  );
  const selectedEntryCount = selectedPaths.length;
  const selectedFileCount = preflightResult?.objectCount;
  const selectedFilesLabel =
    selectedFileCount == null
      ? `${selectedEntryCount} selected ${selectedEntryCount === 1 ? 'file' : 'files'}`
      : `${selectedFileCount} selected ${selectedFileCount === 1 ? 'file' : 'files'}`;
  const selectedDetailPaths = preflightResult?.resolvedDestinationKeys || selectedPaths;
  const indexedFileCount = recursiveScopedEntries.length;
  const isRecursiveIndexing =
    recursiveBrowseQuery.isLoading || loadMoreRecursiveEntriesMutation.isLoading || Boolean(recursiveContinuationToken);

  const clearPreflightReview = () => {
    setPreflightResult(undefined);
    setOverwriteApproved(false);
    preflightMutation.reset();
  };

  const changeBucket = (newBucketKey: string) => {
    setSelectedBucketKey(newBucketKey);
    setBrowsePrefix('');
    setAdditionalEntries([]);
    setNextContinuationToken(undefined);
    setRecursiveEntries([]);
    setRecursiveContinuationToken(undefined);
    setLoadedFoldersByPath({});
    setQuickFilter('');
    setSelectedPaths([]);
    setIsSelectedFilesListOpen(false);
    setDestinationConflictPolicy('fail');
    setRenameDestinationPaths({});
    clearPreflightReview();
  };

  useEffect(() => {
    if (selectedBucketKey || bucketSettings.length !== 1) {
      return;
    }

    changeBucket(bucketSettingKey(bucketSettings[0]));
  }, [bucketSettings, selectedBucketKey]);

  const navigateToPrefix = (newPrefix: string) => {
    setBrowsePrefix(newPrefix);
    setAdditionalEntries([]);
    setNextContinuationToken(undefined);
    setQuickFilter('');
  };

  const changeSelectedPaths = (nextSelectedPaths: string[]) => {
    setSelectedPaths(nextSelectedPaths);
    clearPreflightReview();
    setIsSelectedFilesListOpen(false);
  };

  function getFolderDescendantFilePaths(folderPath: string): string[] {
    return (recursiveTree.foldersByPath[folderPath] || []).flatMap((entry) =>
      entry.entryType === 'folder' ? getFolderDescendantFilePaths(entry.sourcePath) : [entry.sourcePath]
    );
  }

  function getSelectableSourcePaths(entry: ScopedBrowseEntry) {
    return entry.entryType === 'folder' ? getFolderDescendantFilePaths(entry.sourcePath) : [entry.sourcePath];
  }

  function getScopedFolderEntries(folderPath: string): ScopedBrowseEntry[] {
    const expandedFolderEntries = map(loadedFoldersByPath[folderPath]?.entries || [], (entry) => ({
      ...entry,
      id: getEntrySourcePath(folderPath, entry.path),
      sourcePath: getEntrySourcePath(folderPath, entry.path),
    }));
    return uniqBy([...(recursiveTree.foldersByPath[folderPath] || []), ...expandedFolderEntries], 'sourcePath');
  }

  const reviewTransfer = () => preflightMutation.mutate(getTransferRequest());

  const startTransfer = () => createTransferMutation.mutate(getTransferRequest());

  if (queuedTransferJobId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, overflowY: 'auto', minHeight: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Transfer queued
          </Typography>
          <Alert severity="success" sx={{ mb: 2 }}>
            External slide transfer {queuedTransferJobId} was queued successfully.
          </Alert>
          <UploadAsyncIngestHandoff studyId={studyId} />
        </Box>
        <DialogChromeFooter sticky>
          <Button variant="contained" onClick={onClose} sx={dialogPrimaryActionSx}>
            Done
          </Button>
        </DialogChromeFooter>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box
        sx={{
          px: 3,
          py: 2,
          overflow: 'hidden',
          minHeight: 0,
          flex: 1,
          display: 'grid',
          gridTemplateRows: 'auto auto minmax(0, 1fr) auto',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Import from configured external storage
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a study source, browse its contents, and select files or folders to copy into this study.
          </Typography>

          {isStudyLoading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Loading study external storage settings...
            </Alert>
          )}
          {isStudyError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Study external storage settings could not be loaded.
            </Alert>
          )}
          {!isStudyLoading && !isStudyError && bucketSettings.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No enabled external slide sources are configured for this study. Configure one in Study settings first.
            </Alert>
          )}
        </Box>

        {bucketSettings.length > 0 && (
          <FormControl fullWidth>
            <InputLabel id="external-slide-source-label">External slide source</InputLabel>
            <Select
              labelId="external-slide-source-label"
              label="External slide source"
              value={selectedBucketKey}
              disabled={isTransferActionLoading}
              onChange={(event) => changeBucket(event.target.value)}
            >
              {map(bucketSettings, (setting) => (
                <MenuItem key={bucketSettingKey(setting)} value={bucketSettingKey(setting)}>
                  {setting.displayName || setting.bucket} ({setting.providerName})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {selectedBucketSetting && (
          <Box sx={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {browseQuery.isLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Validating access and loading contents...</Typography>
              </Box>
            )}
            {browseQuery.isError && <Alert severity="error">{getErrorMessage(browseQuery.error)}</Alert>}
            {browseQuery.isSuccess && (
              <>
                <Box
                  role="status"
                  aria-live="polite"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: isRecursiveIndexing ? 'action.hover' : 'transparent',
                  }}
                >
                  {isRecursiveIndexing && <CircularProgress size={16} />}
                  <Typography variant="caption" color="text.secondary">
                    {isRecursiveIndexing
                      ? `Indexing files for quick filter... ${indexedFileCount} loaded`
                      : `Quick filter indexed ${indexedFileCount} ${indexedFileCount === 1 ? 'file' : 'files'}`}
                  </Typography>
                </Box>
                {recursiveBrowseQuery.isError && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Quick filter could not index nested files. You can still open folders to browse.
                  </Alert>
                )}
                {loadMoreRecursiveEntriesMutation.isError && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Quick filter stopped before all nested files were indexed. You can still open folders to browse.
                  </Alert>
                )}
                <FileTreeSelector
                  title={browsePrefix || 'Bucket root'}
                  subtitle={selectedBucketSetting.bucket}
                  headerAction={
                    browsePrefix ? (
                      <Button onClick={() => navigateToPrefix(getParentPrefix(browsePrefix))}>Up one folder</Button>
                    ) : null
                  }
                  rootEntries={treeRootEntries}
                  selectedItemIds={selectedPaths}
                  quickFilter={quickFilter}
                  onQuickFilterChange={setQuickFilter}
                  onSelectedItemIdsChange={changeSelectedPaths}
                  getChildEntries={(entry) => getScopedFolderEntries(entry.sourcePath)}
                  getSelectableItemIds={getSelectableSourcePaths}
                  getEntryItemCount={(entry) =>
                    entry.entryType === 'folder' ? size(getFolderDescendantFilePaths(entry.sourcePath)) : undefined
                  }
                  onFolderExpansionChange={(entry, isExpanded) => {
                    if (
                      isExpanded &&
                      !loadedFoldersByPath[entry.sourcePath] &&
                      !recursiveTree.foldersByPath[entry.sourcePath]?.length
                    ) {
                      loadFolderMutation.mutate(entry.sourcePath);
                    }
                  }}
                  renderEntrySecondary={(entry, isFolder) =>
                    isFolder || entry.size == null ? undefined : formatBytes(entry.size)
                  }
                  renderEntrySecondaryAction={(entry, isFolder) =>
                    isFolder ? (
                      <Button
                        disabled={isTransferActionLoading}
                        onClick={(event) => {
                          event.stopPropagation();
                          navigateToPrefix(entry.sourcePath);
                        }}
                      >
                        Open
                      </Button>
                    ) : undefined
                  }
                  renderEntryStatus={(entry, depth, isExpanded) => {
                    const folderState = loadedFoldersByPath[entry.sourcePath];
                    const childEntries = getScopedFolderEntries(entry.sourcePath);

                    if (!isExpanded || entry.entryType !== 'folder' || childEntries.length > 0) {
                      return null;
                    }

                    if (folderState?.isLoading) {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, pl: 6 + depth * 2 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">
                            Loading folder...
                          </Typography>
                        </Box>
                      );
                    }

                    if (folderState?.error) {
                      return (
                        <Alert severity="error" sx={{ ml: 4 + depth * 2 }}>
                          {getErrorMessage(folderState.error)}
                        </Alert>
                      );
                    }

                    return folderState ? (
                      <Typography color="text.secondary" sx={{ py: 1, pl: 6 + depth * 2 }}>
                        This folder is empty.
                      </Typography>
                    ) : null;
                  }}
                  searchDisabled={isTransferActionLoading}
                  interactionDisabled={isTransferActionLoading}
                  listAriaLabel="external storage contents"
                  listSx={{ flex: 1, minHeight: 0 }}
                />
                {nextContinuationToken && (
                  <Box sx={{ p: 1.5, textAlign: 'center' }}>
                    <Button disabled={loadMoreMutation.isLoading} onClick={() => loadMoreMutation.mutate()}>
                      {loadMoreMutation.isLoading ? 'Loading more...' : 'Load more'}
                    </Button>
                  </Box>
                )}
                {loadMoreMutation.isError && (
                  <Alert severity="error">{getErrorMessage(loadMoreMutation.error as ResponseError)}</Alert>
                )}
              </>
            )}
          </Box>
        )}

        {selectedBucketSetting && (
          <Box sx={{ minHeight: 0, maxHeight: { xs: 180, md: '24vh' }, overflowY: 'auto', pr: 1 }}>
            {selectedPaths.length === 0 ? (
              <Typography variant="subtitle2">Select at least one file or folder to start a transfer.</Typography>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setIsSelectedFilesListOpen((isOpen) => !isOpen)}
                  sx={{ px: 0, minWidth: 0 }}
                >
                  {selectedFilesLabel}
                </Button>
                {!preflightResult && (
                  <Typography variant="caption" color="text.secondary">
                    Review transfer to verify selected files.
                  </Typography>
                )}
              </Box>
            )}

            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  checked={generateCsv}
                  disabled={isTransferActionLoading}
                  onChange={(event) => {
                    setGenerateCsv(event.target.checked);
                    setIsCsvTouched(false);
                    clearPreflightReview();
                  }}
                />
              }
              label="Create a transferred-files CSV"
            />
            {generateCsv && (
              <TextField
                fullWidth
                required
                disabled={isTransferActionLoading}
                label="CSV file name"
                value={csvFileName}
                onChange={(event) => {
                  setCsvFileName(event.target.value);
                  setIsCsvTouched(true);
                  clearPreflightReview();
                }}
                onBlur={() => setIsCsvTouched(true)}
                error={isCsvTouched && !isCsvFileNameValid}
                helperText={
                  isCsvTouched && !isCsvFileNameValid
                    ? 'Enter a file name ending in .csv without folders.'
                    : 'The CSV will list the files transferred by this job.'
                }
                sx={{ mt: 1 }}
              />
            )}

            <Accordion disableGutters variant="outlined" sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Advanced settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  fullWidth
                  type="number"
                  required
                  disabled={isTransferActionLoading}
                  label="Files per worker"
                  value={transferBatchSize}
                  inputProps={{ min: 1, step: 1 }}
                  onChange={(event) => {
                    setTransferBatchSize(event.target.value);
                    clearPreflightReview();
                  }}
                  error={!isTransferBatchSizeValid}
                  helperText={
                    isTransferBatchSizeValid
                      ? 'Each worker receives one explicit batch with up to this many files.'
                      : 'Enter a positive whole number.'
                  }
                />
              </AccordionDetails>
            </Accordion>

            <FormControl component="fieldset" disabled={isTransferActionLoading} sx={{ mt: 2 }}>
              <FormLabel component="legend">If a destination file already exists</FormLabel>
              <RadioGroup
                value={destinationConflictPolicy}
                onChange={(event) => {
                  setDestinationConflictPolicy(event.target.value as ExternalSlideTransferDestinationConflictPolicy);
                  setRenameDestinationPaths({});
                  clearPreflightReview();
                }}
                sx={{ mt: 0.5 }}
              >
                <FormControlLabel value="fail" control={<Radio />} label="Stop the transfer" />
                <FormControlLabel value="skip" control={<Radio />} label="Skip existing files" />
                <FormControlLabel value="overwrite" control={<Radio />} label="Overwrite existing files" />
                <FormControlLabel value="rename" control={<Radio />} label="Rename incoming files" />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary">
                Review checks the current destination before any transfer job is created.
              </Typography>
            </FormControl>
          </Box>
        )}

        {preflightMutation.isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Checking sources and destination...</Typography>
          </Box>
        )}
        {preflightMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {getErrorMessage(preflightMutation.error as ResponseError)}
          </Alert>
        )}

        {preflightResult && (
          <Box sx={{ mt: 2 }} aria-label="Transfer review">
            <Alert severity={preflightResult.launchBlocked ? 'warning' : 'success'}>
              Checked {preflightResult.objectCount} {preflightResult.objectCount === 1 ? 'object' : 'objects'}
              {preflightResult.totalSizeBytes == null ? '' : ` (${formatBytes(preflightResult.totalSizeBytes)})`}.
              {` ${preflightResult.workloadCount} ${
                preflightResult.workloadCount === 1 ? 'worker batch' : 'worker batches'
              } at up to ${preflightResult.transferBatchSize} files each.`}
              {preflightResult.existingActiveJobId
                ? ' An identical transfer is already active.'
                : preflightResult.launchBlocked
                ? ' Resolve the items below before starting.'
                : ' Ready to transfer.'}
            </Alert>

            {map(take(displayedPreflightWarnings, WARNING_DISPLAY_LIMIT), (warning, index) => (
              <Alert
                key={`${warning.warningType}:${warning.sourcePath || index}`}
                severity={warning.warningType === 'identical_active_transfer' ? 'info' : 'warning'}
                sx={{ mt: 1 }}
              >
                {warning.message}
                {warning.sourcePath ? ` Source: ${warning.sourcePath}` : ''}
              </Alert>
            ))}
            {displayedPreflightWarnings.length > WARNING_DISPLAY_LIMIT && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {displayedPreflightWarnings.length - WARNING_DISPLAY_LIMIT} additional warnings are included in this
                review.
              </Typography>
            )}

            {destinationConflictPolicy === 'rename' && renameConflicts.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">
                  Rename conflicts {renameConflictPage * RENAME_CONFLICT_PAGE_SIZE + 1}-
                  {Math.min((renameConflictPage + 1) * RENAME_CONFLICT_PAGE_SIZE, renameConflicts.length)} of{' '}
                  {renameConflicts.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Enter a new relative path inside the study Incoming folder for every conflict, then check again.
                </Typography>
                {map(visibleRenameConflicts, (conflict) => (
                  <TextField
                    key={conflict.sourcePath}
                    fullWidth
                    required
                    label={`New destination for ${conflict.sourcePath}`}
                    value={renameDestinationPaths[conflict.sourcePath] || ''}
                    onChange={(event) => {
                      setRenameDestinationPaths((currentPaths) => ({
                        ...currentPaths,
                        [conflict.sourcePath]: event.target.value,
                      }));
                      setPreflightResult((currentResult) =>
                        currentResult ? { ...currentResult, launchBlocked: true } : currentResult
                      );
                    }}
                    helperText="Use a relative path such as case-a/slide-copy.svs."
                    sx={{ mt: 1 }}
                  />
                ))}
                {renameConflictPageCount > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Button
                      disabled={renameConflictPage === 0}
                      onClick={() => setRenameConflictPage((currentPage) => currentPage - 1)}
                    >
                      Previous conflicts
                    </Button>
                    <Button
                      disabled={renameConflictPage === renameConflictPageCount - 1}
                      onClick={() => setRenameConflictPage((currentPage) => currentPage + 1)}
                    >
                      Next conflicts
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {requiresOverwriteApproval && !preflightResult.launchBlocked && (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={overwriteApproved}
                    onChange={(event) => setOverwriteApproved(event.target.checked)}
                  />
                }
                label={`I approve overwriting ${preflightResult.destinationConflicts.length} existing ${
                  preflightResult.destinationConflicts.length === 1 ? 'file' : 'files'
                }`}
              />
            )}

            {preflightResult.existingActiveJobId && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Transfer {preflightResult.existingActiveJobId} is already active. No duplicate job will be created.
              </Alert>
            )}
          </Box>
        )}

        {createTransferMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {getErrorMessage(createTransferMutation.error as ResponseError)}
          </Alert>
        )}

        {preflightResult && !preflightResult.launchBlocked && (
          <Box sx={{ mt: 2 }}>
            <StudyCohortManifestStep />
          </Box>
        )}
      </Box>

      <DialogChromeFooter sticky>
        <Button onClick={onClose}>Cancel</Button>
        {preflightResult?.existingActiveJobId ? (
          <Button variant="contained" onClick={onClose} sx={dialogPrimaryActionSx}>
            Done
          </Button>
        ) : preflightResult && !preflightResult.launchBlocked ? (
          <Button
            variant="contained"
            disabled={(requiresOverwriteApproval && !overwriteApproved) || createTransferMutation.isLoading}
            onClick={startTransfer}
            sx={dialogPrimaryActionSx}
          >
            {createTransferMutation.isLoading ? 'Starting transfer...' : 'Start transfer'}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={
              !canReviewTransfer ||
              preflightMutation.isLoading ||
              Boolean(
                preflightResult?.launchBlocked && (destinationConflictPolicy !== 'rename' || !allRenamePathsProvided)
              )
            }
            onClick={reviewTransfer}
            sx={dialogPrimaryActionSx}
          >
            {preflightMutation.isLoading
              ? 'Reviewing transfer...'
              : preflightResult?.launchBlocked && destinationConflictPolicy === 'rename'
              ? 'Check renamed paths'
              : 'Review transfer'}
          </Button>
        )}
      </DialogChromeFooter>
      <Dialog
        open={isSelectedFilesListOpen && selectedDetailPaths.length > 0}
        onClose={() => setIsSelectedFilesListOpen(false)}
        fullWidth
        maxWidth="md"
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: 'calc(100vh - 96px)',
          },
          '& .MuiDialogContent-root': {
            p: 0,
          },
        }}
      >
        <DialogChromeHeader
          title={selectedFilesLabel}
          subtitle="Selected files for this transfer."
          onClose={() => setIsSelectedFilesListOpen(false)}
        />
        <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <List dense disablePadding aria-label="Selected file list">
            {map(selectedDetailPaths, (path) => (
              <ListItem
                key={path}
                divider
                secondaryAction={
                  preflightResult || isTransferActionLoading ? undefined : (
                    <Button
                      size="small"
                      onClick={() =>
                        changeSelectedPaths(filter(selectedPaths, (selectedPath) => selectedPath !== path))
                      }
                    >
                      Remove
                    </Button>
                  )
                }
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <InsertDriveFileOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={path} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogChromeFooter>
          <Button onClick={() => setIsSelectedFilesListOpen(false)}>Close</Button>
        </DialogChromeFooter>
      </Dialog>
    </Box>
  );
};

export default ExternalStorageTransferForm;
